<script setup>
import { computed, ref, onMounted, getCurrentInstance, watch, useTemplateRef, onBeforeUnmount } from 'vue'
import _ from 'lodash'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification"
import IconBase from '@/components/icon/IconBase.vue'
import IconClose from '@/components/icon/IconClose.vue'
import IconSearch from '@/components/icon/IconSearch.vue'
import PlatformItem from "@/components/PlatformItem.vue"
import PlatformDetails from "@/components/PlatformDetails.vue"
import Sidebar from './Sidebar.vue'
import FormButton from '../Atoms/FormButton.vue'
import FormInput from '../Atoms/FormInput.vue'

const instance = getCurrentInstance()
const root = instance.proxy.$root
const route = useRoute()
const router = useRouter()
const store = useStore()
const { notify } = useNotification()

// Refs
const sidebar = useTemplateRef('sidebar')
const searchQuery = ref('')

// Computed
const platforms = computed(() => store.getters['Destination/platforms'])
const isPlatformSelected = computed(() => store.getters['Destination/isPlatformSelected'])
const selectedPlatform = computed(() => store.getters['Destination/selectedPlatform'])
const currentStage = computed(() => isPlatformSelected.value ? 2 : 1)
const filteredPlatforms = computed(() => {
	const query = searchQuery.value.trim().toLowerCase()
	if (!query) return platforms.value
	return (platforms.value || []).filter(platform => platform?.name?.toLowerCase().includes(query))
})

// Methods
const clearSelected = () => {
	searchQuery.value = ''
	store.dispatch('Destination/clearSelectedPlatform')
	store.dispatch('Destination/clearDestination')
}
const toggleSidebar = () => {
	sidebar.value.toggleSidebar()
}

const goToStage = () => clearSelected()

defineExpose({ toggleSidebar })
</script>

<template>
	<Sidebar ref="sidebar" :stage="currentStage" @closed="clearSelected" @go-to-stage="goToStage" :isMultiStage="true"
		placement="left" hide-header width="md:w-[516px]" height="h-[80vh]">
		<template v-slot:stage-one>
			<div class="flex flex-col h-full">
				<div class="pt-6 pb-3 px-4 flex items-start justify-between gap-3 shrink-0">
					<div class="min-w-0">
						<h1 class="text-lg font-semibold text-white mb-0.5">New destination</h1>
						<p class="text-tiny text-surface-7 mb-0">Choose a platform to send your stream to.</p>
					</div>
					<FormButton size="sm" is-icon type="link" @click="toggleSidebar">
						<icon-base width-view-box="12" height-view-box="12" iconColor="none" width="12" height="12"
							class="text-surface-7">
							<icon-close />
						</icon-base>
					</FormButton>
				</div>
				<div class="px-4 pb-3 shrink-0">
					<div class="relative">
						<icon-base class="absolute left-3 top-1/2 -translate-y-1/2 text-surface-7 pointer-events-none z-10">
							<icon-search />
						</icon-base>
						<FormInput v-model="searchQuery" placeholder="Search platforms…" customClasses="!pl-9" />
					</div>
				</div>
				<ul class="grid grid-cols-3 gap-3 px-4 pb-6 overflow-y-auto overflow-x-hidden grow">
					<li v-if="!searchQuery.trim()">
						<PlatformItem />
					</li>
					<li v-for="(item, index) in filteredPlatforms" :key="index">
						<PlatformItem :platform="item" />
					</li>
					<li v-if="searchQuery.trim() && filteredPlatforms.length === 0"
						class="col-span-3 text-center text-tiny text-surface-6 py-8">
						No platforms match “{{ searchQuery.trim() }}”.
					</li>
				</ul>
			</div>
		</template>
		<template v-slot:stage-two>
			<PlatformDetails :close="toggleSidebar" @close-modal="toggleSidebar" />
		</template>
	</Sidebar>

</template>

<style></style>